import type { GameEventController } from '../types';
import { openPiggyBankOverlay } from './behaviors/openPiggyBankOverlay';
import * as PiggyBankUtils from './PiggyBankUtils';
import { PiggyBankTier } from './types';
import type { PiggyBankDependencies } from './types';
import { PIGGY_BANK_TIER_CONFIG_MAP } from './PiggyBankConstants';

export class PiggyBankController implements GameEventController {
  public readonly featureName = 'Piggy Bank';

  private playLevelAction: (() => Promise<void>) | null = null;

  constructor(private readonly dependencies: PiggyBankDependencies) {
    const { observable } = this.dependencies;

    const piggyBankState = PiggyBankUtils.getPiggyBankState(observable);
    observable.updateValue({
      piggyBank: {
        ...piggyBankState,
        //Reset the flag on game launch, so that it opens one per session when full
        fullPiggyBankShown: false,
      },
    });
  }

  public async startMainMenuFlow(): Promise<void> {
    const { observable } = this.dependencies;
    const piggyBankState = PiggyBankUtils.getPiggyBankState(observable);

    PiggyBankUtils.onboardPiggyBankTier(observable);

    let hasLastChance = false;
    let hasResetPiggyBank = false;

    if (PiggyBankUtils.isPiggyBankExpired(observable)) {
      if (PiggyBankUtils.isPiggyBankFull(observable)) {
        hasLastChance = true;
      } else {
        PiggyBankUtils.decreasePiggyBankTierIfNeeded(observable);
        hasResetPiggyBank = true;
      }
    }

    const shouldOpenFullPiggyBank =
      PiggyBankUtils.isPiggyBankAvailable(observable) &&
      PiggyBankUtils.isPiggyBankFull(observable) &&
      (!piggyBankState.fullPiggyBankShown || hasLastChance);

    if (hasResetPiggyBank || shouldOpenFullPiggyBank) {
      if (shouldOpenFullPiggyBank) {
        observable.updateValue({
          piggyBank: {
            ...piggyBankState,
            fullPiggyBankShown: true,
          },
        });
      }

      const openPiggyBank = async (): Promise<void> => {
        await openPiggyBankOverlay({
          dependencies: this.dependencies,
          onPlayClicked: async () => {
            await this.playLevelAction?.();
          },
          onPiggyBankTierIncreased: async () => {
            await openPiggyBank();
          },
        });
      };

      await openPiggyBank();
    }
  }

  public setPlayLevelAction(action: () => Promise<void>): void {
    this.playLevelAction = action;
  }

  public onLevelWon(): void {
    const { observable } = this.dependencies;
    const piggyBank = PiggyBankUtils.getPiggyBankState(observable);

    observable.updateValue({
      piggyBank: PiggyBankUtils.addCoins(piggyBank, PIGGY_BANK_TIER_CONFIG_MAP[piggyBank.currentTier].coinsPerLevel),
    });
  }

  public getDebugFunctions(): Record<string, () => void> {
    const { observable } = this.dependencies;

    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      'Fill piggy bank': (): void => {
        const piggyBankState = PiggyBankUtils.getPiggyBankState(observable);
        const coinsToAdd = piggyBankState.maxCoins - piggyBankState.coins;
        observable.updateValue({
          piggyBank: PiggyBankUtils.addCoins({ ...piggyBankState }, coinsToAdd),
        });
      },
      'Empty piggy bank': (): void => {
        const piggyBankState = PiggyBankUtils.getPiggyBankState(observable);
        observable.updateValue({
          piggyBank: {
            ...piggyBankState,
            coins: 0,
          },
        });
      },
      '+ 100 coins': (): void => {
        const piggyBankState = PiggyBankUtils.getPiggyBankState(observable);
        observable.updateValue({
          piggyBank: PiggyBankUtils.addCoins({ ...piggyBankState }, 100),
        });
      },
      '+ 1000 coins': (): void => {
        const piggyBankState = PiggyBankUtils.getPiggyBankState(observable);
        observable.updateValue({
          piggyBank: PiggyBankUtils.addCoins({ ...piggyBankState }, 1000),
        });
      },
      '+ 10000 coins': (): void => {
        const piggyBankState = PiggyBankUtils.getPiggyBankState(observable);
        observable.updateValue({
          piggyBank: PiggyBankUtils.addCoins({ ...piggyBankState }, 10000),
        });
      },
      'Increase piggy bank tier': (): void => {
        PiggyBankUtils.increasePiggyBankTier(observable);
      },
      'Decrease piggy bank tier': (): void => {
        PiggyBankUtils.decreasePiggyBankTierIfNeeded(observable, true);
      },
      'Reset piggy bank state': (): void => {
        observable.updateValue({
          piggyBank: PiggyBankUtils.getPiggyBankDefaultStateByTier(
            PiggyBankUtils.arePiggyBankTiersActive() ? PiggyBankTier.Normal : PiggyBankTier.Legacy,
          ),
        });
      },
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }
}
